import React from 'react';
import PropTypes from 'prop-types';
import { extractFormToken } from '../../../../Services/adminApiService';
import EmailStatusBadge from './EmailStatusBadge';
import PdfDownloadButton from './PdfDownloadButton';
import ResendEmailButton from './ResendEmailButton';

/**
 * FormTable - Table display for form submissions
 *
 * Displays forms in a tabular format with:
 * - ID, Type, Submitted By, Date columns
 * - View action button
 * - Empty state handling
 *
 * @param {Object} props
 */
const FormTable = ({ forms, currentPage, totalPages, onViewForm, loading }) => {
  if (loading) {
    return (
      <div className="section-card">
        <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
          Loading forms...
        </div>
      </div>
    );
  }

  return (
    <div className="section-card">
      <h2>Form Details</h2>
      <p style={{ color: '#718096', marginBottom: '16px' }}>
        Showing {forms.length} forms on page {currentPage + 1} of{' '}
        {totalPages || 0}
      </p>
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Type</th>
              <th>Submitted By</th>
              <th>Date</th>
              <th>Email Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {forms.length > 0 ? (
              forms.map((form, formIndex) => (
                <FormTableRow
                  key={form.id || formIndex}
                  form={form}
                  onView={() => onViewForm(form, form.formType)}
                />
              ))
            ) : (
              <tr>
                <td
                  colSpan="6"
                  style={{
                    textAlign: 'center',
                    padding: '20px',
                    color: '#718096',
                  }}
                >
                  No forms found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/**
 * FormTableRow - Individual row in the forms table
 */
const FormTableRow = ({ form, onView }) => {
  const submittedDate = form.created || form.submittedDate;
  const formattedDate = submittedDate
    ? new Date(submittedDate).toLocaleDateString()
    : 'Unknown';
  const tokenInfo = extractFormToken(form.pdfDownloadUrl);

  return (
    <tr>
      <td>
        <span className="username-badge">#{form.id}</span>
      </td>
      <td>
        <span className="form-type-badge">
          {form.formType?.charAt(0).toUpperCase() + form.formType?.slice(1) ||
            'Unknown'}
        </span>
      </td>
      <td>{form.portalUser?.userName || 'Unknown'}</td>
      <td style={{ fontSize: '13px' }}>{formattedDate}</td>
      <td>
        {form.emailStatus ? (
          <EmailStatusBadge status={form.emailStatus} />
        ) : (
          <span style={{ color: '#a0aec0', fontStyle: 'italic' }}>N/A</span>
        )}
      </td>
      <td>
        <div className="form-action-buttons">
          <button className="btn-view" onClick={onView}>
            View Details
          </button>
          {form.pdfDownloadUrl && (
            <PdfDownloadButton
              pdfDownloadUrl={form.pdfDownloadUrl}
              formName={form.formType}
            />
          )}
          {form.emailStatus === 'FAILED' && tokenInfo && (
            <ResendEmailButton
              formType={tokenInfo.formType}
              formToken={tokenInfo.formToken}
            />
          )}
        </div>
      </td>
    </tr>
  );
};

FormTable.propTypes = {
  forms: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      formType: PropTypes.string,
      portalUser: PropTypes.shape({
        userName: PropTypes.string,
      }),
      created: PropTypes.string,
      submittedDate: PropTypes.string,
    })
  ).isRequired,
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onViewForm: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

FormTable.defaultProps = {
  loading: false,
};

FormTableRow.propTypes = {
  form: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    formType: PropTypes.string,
    portalUser: PropTypes.shape({
      userName: PropTypes.string,
    }),
    created: PropTypes.string,
    submittedDate: PropTypes.string,
  }).isRequired,
  onView: PropTypes.func.isRequired,
};

export { FormTable, FormTableRow };
export default FormTable;
